import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { apiClient } from '@/lib/apiClient'

import type {
  ApplicationAccepted,
  ApplicationPayload,
  CandidateResult,
  JurySheet,
  PublicAward,
  PublicCandidate,
  PublicCategory,
  PublicContestStandings,
} from './types'

export function useCompetitionCategories(slug: string | undefined) {
  const { i18n } = useTranslation()

  return useQuery({
    queryKey: ['competition-categories', slug, i18n.language],
    queryFn: () =>
      apiClient.get<PublicCategory[]>(`/public/events/${slug}/competition/categories`, {
        locale: i18n.language,
      }),
    enabled: Boolean(slug),
  })
}

export function useCompetitionCandidates(slug: string | undefined) {
  const { i18n } = useTranslation()

  return useQuery({
    queryKey: ['competition-candidates', slug, i18n.language],
    queryFn: () =>
      apiClient.get<PublicCandidate[]>(`/public/events/${slug}/competition/candidates`, {
        locale: i18n.language,
      }),
    enabled: Boolean(slug),
  })
}

/**
 * Récompenses.
 *
 * `results` reste vide tant que l'équipe n'a pas révélé le podium : le front
 * n'a rien à masquer lui-même, le serveur ne lui envoie simplement pas.
 * Rafraîchi le soir J pour que l'annonce apparaisse sans recharger.
 */
export function useAwards(slug: string | undefined) {
  const { i18n } = useTranslation()

  return useQuery({
    queryKey: ['awards', slug, i18n.language],
    queryFn: () =>
      apiClient.get<PublicAward[]>(`/public/events/${slug}/awards`, { locale: i18n.language }),
    enabled: Boolean(slug),
    refetchInterval: 60_000,
  })
}

export function useApply(slug: string | undefined) {
  return useMutation({
    mutationFn: (payload: ApplicationPayload) =>
      apiClient.post<ApplicationAccepted>(
        `/public/events/${slug}/competition/applications`,
        payload,
      ),
  })
}

/**
 * Le classement général de chaque titre — vide tant que tout n'est pas publié.
 *
 * Rafraîchi comme la page « Résultats » qui l'affiche : la dernière
 * publication tombe au micro, et la salle doit la voir sans recharger.
 */
export function usePublicStandings(
  slug: string | undefined,
  options: { refetchInterval?: number } = {},
) {
  const { i18n } = useTranslation()

  return useQuery({
    queryKey: ['competition-standings', slug, i18n.language],
    queryFn: () =>
      apiClient.get<PublicContestStandings[]>(`/public/events/${slug}/competition/standings`, {
        locale: i18n.language,
      }),
    enabled: Boolean(slug),
    refetchInterval: options.refetchInterval ?? 60_000,
  })
}

/**
 * La fiche privée d'un candidat. Le jeton est celui du lien reçu par e-mail ;
 * un lien refusé ne guérira pas, on ne réessaie donc pas.
 */
export function useCandidateResult(token: string | null, candidateId: string | null) {
  const { i18n } = useTranslation()

  return useQuery({
    queryKey: ['candidate-result', token, candidateId, i18n.language],
    queryFn: () =>
      apiClient.get<CandidateResult>(
        `/me/competition/results?token=${encodeURIComponent(token ?? '')}` +
          `&candidate_id=${encodeURIComponent(candidateId ?? '')}`,
        { locale: i18n.language },
      ),
    enabled: Boolean(token && candidateId),
    retry: false,
    // Relue chaque minute : une publication retirée par l'équipe (« on s'est
    // trompé ») doit disparaître aussi d'une fiche restée ouverte. Un lien
    // refusé ne guérira pas : on cesse alors de relire.
    refetchInterval: (query) => (query.state.status === 'error' ? false : 60_000),
  })
}

function jurorQuery(token: string, jurorId: string): string {
  return `token=${encodeURIComponent(token)}&juror_id=${encodeURIComponent(jurorId)}`
}

/**
 * La feuille d'un juré : ses tours, ses candidats, SES notes.
 *
 * Rafraîchie en continu : un tour s'ouvre au micro, et le juré doit pouvoir
 * noter sans recharger sa page. Un lien refusé ne guérira pas : pas de
 * nouvel essai.
 */
export function useJurySheet(token: string | null, jurorId: string | null) {
  const { i18n } = useTranslation()

  return useQuery({
    queryKey: ['jury-sheet', token, jurorId, i18n.language],
    queryFn: () =>
      apiClient.get<JurySheet>(`/me/jury?${jurorQuery(token ?? '', jurorId ?? '')}`, {
        locale: i18n.language,
      }),
    enabled: Boolean(token && jurorId),
    retry: false,
    refetchInterval: 15_000,
  })
}

/** Donner (ou corriger) une note — tant que le tour est ouvert. */
export function useJuryScore(token: string | null, jurorId: string | null) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      roundId,
      candidateId,
      score,
    }: {
      roundId: string
      candidateId: string
      score: number
    }) =>
      apiClient.put<{ vote_category_id: string; candidate_id: string; score: number }>(
        `/me/jury/rounds/${roundId}/candidates/${candidateId}?${jurorQuery(
          token ?? '',
          jurorId ?? '',
        )}`,
        { score },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['jury-sheet', token, jurorId] })
    },
  })
}
